/**
 * useGoogleOAuth - Hook for handling first-party Google OAuth flow
 * 
 * Uses OUR Workspace OAuth client (not Emergent's shared client).
 * Flow:
 * 1. User clicks "Continue with Google"
 * 2. Redirect to /api/auth/google/start (backend builds Google OAuth URL with our client_id)
 * 3. Google authenticates user and redirects to /api/auth/google/callback
 * 4. Backend exchanges code, validates domain, creates/updates user, issues JWT
 * 5. Backend redirects to /auth/callback#token=xxx
 * 6. Frontend extracts token and completes login
 */
import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";  // Empty = same origin (relative URLs)

export function useGoogleOAuth({ onSuccess, onError }) {
  const [loading, setLoading] = useState(false);
  const [oauthConfigured, setOauthConfigured] = useState(null);

  // Check if OAuth is configured on mount
  useEffect(() => {
    const checkOAuthConfig = async () => {
      try {
        const response = await axios.get(`${API}/api/auth/google/config`);
        // Check for both enabled field and legacy oauth_configured field
        // Also handle case where endpoint returns error_code field
        if (response.data.error_code) {
          // Not fatal - just disable Google login
          console.warn("OAuth config returned error_code:", response.data.error_code);
          setOauthConfigured(false);
        } else {
          setOauthConfigured(response.data.enabled || response.data.oauth_configured || false);
        }
      } catch (err) {
        console.error("Failed to check OAuth config:", err);
        setOauthConfigured(false);
      }
    };
    checkOAuthConfig();
  }, []);

  // Start first-party OAuth flow
  const startOAuth = useCallback(() => {
    if (loading) return;

    setLoading(true);

    // Get current path to redirect back after login
    const currentPath = window.location.pathname;
    const redirectAfter = currentPath !== '/login' ? currentPath : '/';

    // Redirect to backend OAuth start endpoint
    // This will redirect to Google with OUR client_id
    const oauthStartUrl = `${API}/api/auth/google/start?redirect_after=${encodeURIComponent(redirectAfter)}`;
    
    // Full page redirect (not popup) for better UX and mobile support
    window.location.href = oauthStartUrl;
  }, [loading]);

  // Handle OAuth callback (called from /auth/callback route)
  const handleCallback = useCallback(async () => {
    // Extract token from URL fragment
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const token = params.get('token');
    const redirect = params.get('redirect') || '/';
    const error = params.get('error');

    // Also check query string for errors
    const queryParams = new URLSearchParams(window.location.search);
    const queryError = queryParams.get('error');
    const queryDomain = queryParams.get('domain');

    if (queryError || error) {
      const errorMsg = queryError || error;
      let userMessage = "Google sign-in failed";
      
      switch (errorMsg) {
        case 'domain_not_allowed':
          userMessage = queryDomain 
            ? `Access denied. Domain '${queryDomain}' is not allowed. Please use your company Google Workspace account.`
            : "Access denied. Please use your company Google Workspace account.";
          break;
        case 'account_deleted':
          userMessage = "This account has been deleted.";
          break;
        case 'oauth_not_configured':
          userMessage = "Google sign-in is not configured. Please contact your administrator.";
          break;
        case 'invalid_state':
          userMessage = "Security validation failed. Please try again.";
          break;
        case 'access_denied':
          userMessage = "Sign-in was cancelled.";
          break;
        default:
          userMessage = `Google sign-in error: ${errorMsg}`;
      }
      
      toast.error(userMessage, { duration: 6000 });
      onError?.(userMessage);
      
      // Clear the URL
      window.history.replaceState({}, document.title, '/login');
      return null;
    }

    if (token) {
      // Clear the URL fragment
      window.history.replaceState({}, document.title, redirect);
      
      onSuccess?.(token, null); // User data will be fetched separately
      return { token, redirect };
    }

    return null;
  }, [onSuccess, onError]);

  return {
    startOAuth,
    handleCallback,
    loading,
    oauthConfigured
  };
}
